/**
 * Personal and workspace background images (SC-158).
 *
 * Every byte here is behind an authenticated route, which changes how images are
 * used compared with the bundled presets: **an `<img>` tag cannot send an
 * `Authorization` header**, so nothing can point at `/api/backgrounds/:id/image`
 * directly. Images are fetched explicitly and turned into `blob:` object URLs,
 * which are same-origin, cannot taint a canvas, and work both as a thumbnail
 * `src` and as the processor's `imagePath`.
 *
 * Object URLs are cached per background id for the life of the page. That is
 * deliberate: the track-processor library caches by path string, so revoking a
 * URL that might be re-selected would break the effect the next time the user
 * picks it. They are released together when the user signs out
 * ({@link releaseAllBackgroundImageUrls}, called by `authService.signOut`), so
 * the next account on this page never inherits the previous one's pictures.
 */
import store from '../stores'
import { getServerBaseUrl } from '../utils/serverUrl'
import { getStoredSessionToken } from '../utils/sessionToken'
import { downscaleBackgroundImage } from '../utils/downscaleImage'
import { AuthMessages } from '../components/AuthenticationDialog.messages'
import { reportEndedSession } from './endedSessionHandover'
import { isMeetingToken } from './roomResolver'
import {
  BACKGROUND_CONTENT_TYPES,
  MAX_BACKGROUND_UPLOAD_BYTES,
  MAX_PERSONAL_BACKGROUNDS,
  MAX_WORKSPACE_BACKGROUNDS,
} from '../../../types/backgroundLibrary'

export type BackgroundLibraryKind = 'workspace' | 'custom'

export interface LibraryBackground {
  id: string
  name: string
  kind: BackgroundLibraryKind
  createdAt?: string
}

/**
 * Limit on the picked file. The server applies the same value to the downscaled
 * image that is actually uploaded. Shared through types/backgroundLibrary.
 */
export { MAX_BACKGROUND_UPLOAD_BYTES }

/**
 * The server's count caps, for telling the user where they stand and for
 * disabling an upload that would be refused anyway.
 *
 * The server enforces them; these exist so someone finds out before choosing a
 * file rather than after uploading one. The server is the authority: if they
 * ever drift (an old bundle after a server change), the server wins and the user
 * sees its message.
 */
export { MAX_PERSONAL_BACKGROUNDS, MAX_WORKSPACE_BACKGROUNDS }

/** What the file picker offers, and what the server will accept. */
export const ACCEPTED_UPLOAD_TYPES = BACKGROUND_CONTENT_TYPES

/**
 * Refuse a file we will not accept, in words the user can act on.
 *
 * **SVG is called out by name.** It is a document rather than a picture — it can
 * contain `<script>`, embedded HTML and external references — and these images
 * are served back from our own origin, so one would run in the context of every
 * colleague who viewed it.
 *
 * Worth knowing: a browser *will* rasterise an SVG into a canvas, so without
 * this check the downscale step would quietly turn one into a JPEG and upload
 * that. Nothing dangerous would be stored, but the user asked for an SVG and
 * would silently get something else. Refusing is the honest answer, and the
 * server refuses raw SVG bytes regardless.
 */
export function rejectUnsupportedUpload(file: File): string | null {
  const type = (file.type || '').toLowerCase()
  const name = (file.name || '').toLowerCase()

  if (type === 'image/svg+xml' || name.endsWith('.svg') || name.endsWith('.svgz')) {
    return 'SVG images are not supported. Please use a JPEG, PNG or WebP.'
  }

  // An empty type happens when the OS cannot identify the file; fall back to the
  // extension rather than assuming it is fine.
  if (type && !(ACCEPTED_UPLOAD_TYPES as readonly string[]).includes(type)) {
    return 'Please choose a JPEG, PNG or WebP image.'
  }

  return null
}

const authHeaders = (token: string): Record<string, string> => ({
  Authorization: `Bearer ${token}`,
})

/** The guest's `mtg_` meeting token from the store, or '' when this tab is not a guest's. */
function guestMeetingToken(): string {
  try {
    const token = store.getState()?.guest?.meetingToken
    return isMeetingToken(token) ? token : ''
  } catch {
    return ''
  }
}

/**
 * What a background request is signed with (SC-439).
 *
 * A meeting guest sends the guest token and **only** that: another tab on the same browser may
 * have left a member's session in storage, and the server must take the guest branch rather than
 * act as that member. Everyone else sends their session token, as before. `token` is '' when
 * there is neither.
 */
export interface BackgroundCredential {
  token: string
  guest: boolean
}

export function backgroundCredential(): BackgroundCredential {
  const guestToken = guestMeetingToken()
  if (guestToken) return { token: guestToken, guest: true }
  return { token: getStoredSessionToken(), guest: false }
}

/**
 * Whether this page can hold personal backgrounds: a member through their session, a meeting
 * guest through their token. Pickers use it to offer or hide "Your images" and upload.
 */
export function canHavePersonalBackgrounds(): boolean {
  return backgroundCredential().token !== ''
}

const endpoint = (path: string): string => `${getServerBaseUrl()}/api/backgrounds${path}`

/**
 * The server's 401 answers that mean the session itself is over: the token
 * matched no live session, or one with no user (requireAuthentication).
 */
const ENDED_SESSION_ERRORS = new Set(['Invalid session token', 'User not found in session'])

/**
 * What a workspace upload or delete says when the caller lacks the permission. The server's RBAC
 * refusal body is `{ error: 'forbidden', reason: 'missing_permission', permission }` (SC-439), whose
 * `error` is a code and not words for a person: it is replaced by these, the same sentences the
 * routes used before.
 */
export const WORKSPACE_ADD_REFUSED = 'Only company admins and managers can add workspace backgrounds'
export const WORKSPACE_REMOVE_REFUSED =
  'Only company admins and managers can remove workspace backgrounds'

async function readError(
  response: Response,
  fallback: string,
  permissionRefusal?: string
): Promise<string> {
  try {
    const body = await response.json()
    if (
      permissionRefusal &&
      response.status === 403 &&
      body?.error === 'forbidden' &&
      body?.reason === 'missing_permission'
    ) {
      return permissionRefusal
    }
    // The ended-session texts are server jargon. One only reaches here for a
    // session already replaced, where it says nothing about the current one.
    return typeof body?.error === 'string' && !ENDED_SESSION_ERRORS.has(body.error)
      ? body.error
      : fallback
  } catch {
    return fallback
  }
}

/**
 * SC-452: whether this answer says the session sent with the request has ended,
 * typically because the account signed in elsewhere.
 *
 * Not every 401 does. With no token there was no session to end (a meeting
 * guest's token is not one), and the server also answers 401 "Authentication failed"
 * when its own session lookup throws, say Back4App rate-limiting or down.
 * Signing a valid user out for either would be wrong, so those keep their
 * ordinary failure. Reads a clone: the caller may still want the body.
 *
 * The token must also still be the stored one when the answer lands: a late
 * 401 for a session that has since been replaced (another account, a fresh
 * sign-in, or nobody) must not end the session that replaced it. A picker
 * still open from the old session then shows its generic failure text, not the
 * server's and not "session expired", which is cosmetic and accepted.
 */
async function isEndedSession(response: Response, sent: BackgroundCredential): Promise<boolean> {
  // A guest has no session to end: their 401 (bad or expired meeting token) is an ordinary
  // failure, and must never sign out a member who happens to be signed in in another tab.
  if (response.status !== 401 || !sent.token || sent.guest) return false
  if (getStoredSessionToken() !== sent.token) return false
  try {
    const body = await response.clone().json()
    return ENDED_SESSION_ERRORS.has(body?.error)
  } catch {
    return false
  }
}

/**
 * Shown when a request never got an answer — offline, DNS, a dropped
 * connection. The browser's own text for that is "Failed to fetch" in Chrome,
 * "Load failed" in Safari and "NetworkError when attempting to fetch resource."
 * in Firefox: none of them tells the user what happened or what to do.
 */
export const UPLOAD_NETWORK_ERROR = 'Upload failed - check your connection and try again.'
export const DELETE_NETWORK_ERROR = 'Remove failed - check your connection and try again.'

/**
 * `fetch`, with a rejection (no response at all) turned into `message`. Errors
 * that do carry a response are left to the caller, which has the server's own
 * explanation to show.
 */
async function fetchOrExplain(
  url: string,
  init: RequestInit,
  message: string
): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (error) {
    console.warn('[SC-158] Background request did not reach the server:', error)
    throw new Error(message)
  }
}

/**
 * How long list and delete may take before they count as failed.
 *
 * `fetch` has no timeout of its own, and the picker keeps its spinner (list) or
 * its buttons disabled (delete) until the call settles, so a stalled server
 * would leave the library unusable until the dialog is closed. Both carry a
 * few hundred bytes, so this is generous on any connection a call can run on.
 */
export const BACKGROUND_REQUEST_TIMEOUT_MS = 10000

/**
 * The same for an upload, which is longer because it sends the image. What is
 * sent is the downscaled JPEG (at most 1920x1080, quality 0.85), typically under
 * 1 MB and a few MB at worst; a minute lets even that finish at well under
 * 1 Mbit/s up while still ending a request that has stopped moving.
 */
export const BACKGROUND_UPLOAD_TIMEOUT_MS = 60000

/**
 * A request ended by its time limit, as opposed to one the server answered. Its
 * own class so callers can tell "no answer" from a refusal without reading the
 * message: after a timeout the server may still have done the work (an upload
 * stored, a delete applied), so the caller's view of the library is stale.
 */
export class RequestTimeoutError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'RequestTimeoutError'
    // Keeps `instanceof` working wherever the class is down-levelled.
    Object.setPrototypeOf(this, RequestTimeoutError.prototype)
  }
}

/**
 * The server answered 5xx to a request that changes the library. Unlike a 4xx it
 * does not say that nothing happened: an upload's row can be kept after its save
 * rejected, so the answer is "failed" while the image is stored. Its own class,
 * like {@link RequestTimeoutError}, so callers decide whether to re-read the list
 * from the type and never from the message.
 */
export class ServerFailureError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ServerFailureError'
    Object.setPrototypeOf(this, ServerFailureError.prototype)
  }
}

/**
 * Whether a failed upload or delete leaves the library in an unknown state, so the
 * caller's list may be stale. A refusal (pre-flight check, 4xx) and a request that
 * never left the browser are not: nothing changed, and re-reading would only
 * waste a call, or fail the same way offline.
 */
export function isOutcomeUnknown(error: unknown): boolean {
  return error instanceof RequestTimeoutError || error instanceof ServerFailureError
}

/**
 * Run a request under a time limit: past it the request is aborted and the
 * result rejects with a {@link RequestTimeoutError} carrying `timeoutMessage`.
 *
 * The abort stops the transfer; the race is what guarantees the answer even if
 * something in between ignores the signal (the same pair as the image fetch).
 * The limit covers reading the body too, since a response that stops halfway
 * would otherwise hang the caller just the same.
 */
async function withRequestTimeout<T>(
  ms: number,
  timeoutMessage: string,
  run: (signal: AbortSignal) => Promise<T>
): Promise<T> {
  const controller = new AbortController()
  let timer: ReturnType<typeof setTimeout> | undefined
  const timedOut = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      console.warn(`[SC-158] Background request took longer than ${ms} ms; giving up:`, timeoutMessage)
      controller.abort()
      reject(new RequestTimeoutError(timeoutMessage))
    }, ms)
  })
  try {
    return await Promise.race([run(controller.signal), timedOut])
  } finally {
    if (timer) clearTimeout(timer)
  }
}

export interface BackgroundLibrary {
  backgrounds: LibraryBackground[]
  /**
   * Whether to offer an "add for the company" control. Always false for a meeting guest. Presentation only — the
   * upload route re-checks the role — but showing every member a button that
   * 403s is a poor way to communicate a permission.
   */
  canManageWorkspace: boolean
  /**
   * The list could not be fetched, so `backgrounds` and `canManageWorkspace`
   * say nothing. Without this an outage read as "you are not an admin" and
   * "0 of 3 used" — both untrue, and the second invites an upload that the
   * real count would refuse.
   */
  loadFailed: boolean
  /**
   * Set when the list failed because the session has ended: words for the user
   * that replace a surface's own "check your connection" load error.
   */
  loadError?: string
}

/** Names of backgrounds seen in a listing or upload, for messages about them. */
const backgroundNames = new Map<string, string>()

function rememberNames(backgrounds: LibraryBackground[]): void {
  for (const background of backgrounds) {
    if (background?.id && typeof background.name === 'string' && background.name.trim()) {
      backgroundNames.set(background.id, background.name)
    }
  }
}

/**
 * The name of a background this page has listed or uploaded, or `null`.
 *
 * Synchronous on purpose: it labels a notice about an effect that just failed,
 * and fetching the list to name an image the server has just refused would
 * only fail the same way.
 */
export function peekBackgroundName(id: string): string | null {
  return backgroundNames.get(id) ?? null
}

/** The caller's own backgrounds plus their company's. Never contains a URL. */
export async function listBackgrounds(): Promise<BackgroundLibrary> {
  const failed = { backgrounds: [], canManageWorkspace: false, loadFailed: true }
  try {
    const credential = backgroundCredential()
    // No session and no guest token: nobody to ask for, and the request could only be refused.
    // That is an empty library, not a load error with a retry that can never succeed.
    if (!credential.token) return { backgrounds: [], canManageWorkspace: false, loadFailed: false }
    return await withRequestTimeout(
      BACKGROUND_REQUEST_TIMEOUT_MS,
      'Listing backgrounds timed out',
      async (signal): Promise<BackgroundLibrary> => {
        const response = await fetch(endpoint('/'), {
          headers: authHeaders(credential.token),
          signal,
        })
        if (!response.ok) {
          console.warn('[SC-158] Could not list backgrounds:', response.status)
          if (await isEndedSession(response, credential)) {
            void reportEndedSession()
            return { ...failed, loadError: AuthMessages.SESSION_EXPIRED }
          }
          return failed
        }
        const body = await response.json()
        const backgrounds: LibraryBackground[] = Array.isArray(body?.backgrounds)
          ? body.backgrounds
          : []
        rememberNames(backgrounds)
        return {
          backgrounds,
          canManageWorkspace: body?.canManageWorkspace === true,
          loadFailed: false,
        }
      }
    )
  } catch (error) {
    // Total by design: a rejected fetch — offline, DNS, CORS — is the same
    // outcome as an error status for the caller, which shows a load error with
    // a retry either way rather than an unhandled rejection and a spinner.
    console.warn('[SC-158] Could not reach the server to list backgrounds:', error)
    return failed
  }
}

export interface UploadBackgroundOptions {
  file: File
  name?: string
  /** 'workspace' requires an admin or manager; the server enforces it. */
  scope: BackgroundLibraryKind
}

/**
 * Downscale and upload. Returns the created row, or throws with a message that
 * is safe to show the user.
 *
 * The size check applies to the picked file, which is never uploaded as is: it
 * is the limit the pickers advertise ("up to 15 MB"), and it bounds what the
 * browser has to decode before downscaling (see `utils/downscaleImage.ts`). Only
 * the downscaled image is sent. The server caps the bytes it receives, which is
 * that downscaled image, not the picked file.
 */
export async function uploadBackground({
  file,
  name,
  scope,
}: UploadBackgroundOptions): Promise<LibraryBackground> {
  const unsupported = rejectUnsupportedUpload(file)
  if (unsupported) throw new Error(unsupported)

  if (file.size > MAX_BACKGROUND_UPLOAD_BYTES) {
    throw new Error('That image is larger than 15 MB. Please choose a smaller one.')
  }

  const { blob } = await downscaleBackgroundImage(file)

  const path = scope === 'workspace' ? '/workspace' : '/'
  const query = name ? `?name=${encodeURIComponent(name)}` : ''
  const credential = backgroundCredential()
  // Only the network part is timed: the downscale above is local work that a
  // slow device may legitimately take a while over. A timeout reads like a
  // dropped connection, which is what it amounts to for the user.
  return withRequestTimeout(BACKGROUND_UPLOAD_TIMEOUT_MS, UPLOAD_NETWORK_ERROR, async (signal) => {
    const response = await fetchOrExplain(
      endpoint(`${path}${query}`),
      {
        method: 'POST',
        headers: { ...authHeaders(credential.token), 'Content-Type': blob.type || 'image/jpeg' },
        body: blob,
        signal,
      },
      UPLOAD_NETWORK_ERROR
    )

    if (await isEndedSession(response, credential)) {
      // In place of the server's own "Invalid session token".
      void reportEndedSession()
      throw new Error(AuthMessages.SESSION_EXPIRED)
    }
    if (!response.ok) {
      const message = await readError(response, 'Could not save that background.', WORKSPACE_ADD_REFUSED)
      throw response.status >= 500 ? new ServerFailureError(message) : new Error(message)
    }

    const body = await response.json()
    const created = body.background as LibraryBackground
    rememberNames([created])
    return created
  })
}

export async function deleteBackground(id: string): Promise<void> {
  const credential = backgroundCredential()
  await withRequestTimeout(BACKGROUND_REQUEST_TIMEOUT_MS, DELETE_NETWORK_ERROR, async (signal) => {
    const response = await fetchOrExplain(
      endpoint(`/${encodeURIComponent(id)}`),
      { method: 'DELETE', headers: authHeaders(credential.token), signal },
      DELETE_NETWORK_ERROR
    )
    if (await isEndedSession(response, credential)) {
      void reportEndedSession()
      throw new Error(AuthMessages.SESSION_EXPIRED)
    }
    // Already gone, typically removed by another admin or another tab: what the
    // user asked for is true, so the tile goes rather than staying with an error.
    if (!response.ok && response.status !== 404) {
      throw new Error(
        await readError(response, 'Could not remove that background.', WORKSPACE_REMOVE_REFUSED)
      )
    }
  })
  releaseBackgroundImageUrl(id)
}

// ---------------------------------------------------------------------------
// Authenticated image → object URL
// ---------------------------------------------------------------------------

const objectUrlCache = new Map<string, string>()
const inFlight = new Map<string, Promise<BackgroundImageLookup>>()
// Bumped by releaseAllBackgroundImageUrls. A download that started before it must not
// mint a URL afterwards: nothing would ever revoke it, and it would be the signed-out
// account's picture in the next one's cache.
let cacheGeneration = 0

/**
 * How long one image download may take before it counts as "could not ask".
 *
 * `fetch` has no timeout of its own, and a stalled server or a half-dead
 * connection can hold one open for minutes. Everything that applies an effect
 * waits on this — and while it waits, a camera that has no effect live yet stays
 * held. A few hundred KB should arrive well within this on any connection a
 * video call can run on; past it the user is told and can retry.
 */
export const BACKGROUND_IMAGE_FETCH_TIMEOUT_MS = 8000

/**
 * The outcome of fetching a background's bytes into a `blob:` URL.
 *
 * `gone` and `unavailable` are told apart because the user is told different
 * things: a deleted image needs another pick, a network failure needs a retry.
 * Neither clears the saved choice.
 */
export type BackgroundImageLookup =
  | { status: 'ok'; url: string }
  /** The server says it is not there, or not ours. Definitive. */
  | { status: 'gone' }
  /** We could not ask — offline, the server erred, or it timed out. Says nothing about the image. */
  | { status: 'unavailable' }

async function requestBackgroundImage(
  id: string,
  signal: AbortSignal
): Promise<BackgroundImageLookup> {
  const generation = cacheGeneration
  try {
    const credential = backgroundCredential()
    const response = await fetch(endpoint(`/${encodeURIComponent(id)}/image`), {
      headers: authHeaders(credential.token),
      signal,
    })

    // 404 (deleted) and 403 (no longer ours) are the only answers that mean the
    // background is really gone. Everything else — 5xx, offline, a timeout — is
    // "we could not ask", and must never be read as deletion: clearing a saved
    // background because the network hiccuped once would lose a choice the user
    // never revoked, on every device.
    if (response.status === 404 || response.status === 403) {
      releaseBackgroundImageUrl(id)
      return { status: 'gone' }
    }
    if (!response.ok) {
      console.warn(`[SC-158] Background ${id} could not be loaded (${response.status})`)
      if (await isEndedSession(response, credential)) void reportEndedSession()
      return { status: 'unavailable' }
    }

    // Bytes are immutable per id, so a revalidation that succeeds keeps the object
    // URL it already has. Minting a second one for the same image would leak the
    // first and invalidate any element already pointing at it.
    const existing = objectUrlCache.get(id)
    if (existing) {
      // The body is never read on this path; left open, the browser keeps the
      // connection busy downloading bytes nobody wants. Best effort: a body
      // that is already locked or used refuses, and that is no failure here.
      void response.body?.cancel().catch(() => undefined)
      return { status: 'ok', url: existing }
    }

    const blob = await response.blob()
    // Timed out while the body was arriving: the caller has been told
    // 'unavailable' already, and an object URL minted now would never be used.
    if (signal.aborted || generation !== cacheGeneration) return { status: 'unavailable' }
    const url = URL.createObjectURL(blob)
    objectUrlCache.set(id, url)
    return { status: 'ok', url }
  } catch (error) {
    console.warn(`[SC-158] Could not reach the server for background ${id}:`, error)
    return { status: 'unavailable' }
  }
}

/**
 * {@link requestBackgroundImage}, bounded by BACKGROUND_IMAGE_FETCH_TIMEOUT_MS.
 *
 * The abort stops the download; the race is what guarantees the answer even
 * if something in between ignores the signal. Either way the shared in-flight
 * entry goes with it, so the next asker starts a fresh request instead of
 * joining one that will never finish.
 */
async function fetchBackgroundImage(id: string): Promise<BackgroundImageLookup> {
  const generation = cacheGeneration
  try {
    return await withRequestTimeout(
      BACKGROUND_IMAGE_FETCH_TIMEOUT_MS,
      `Background ${id} could not be loaded in time`,
      (signal) => requestBackgroundImage(id, signal)
    )
  } catch (error) {
    // Only the bound can end up here: the request itself is total.
    if (error instanceof RequestTimeoutError) return { status: 'unavailable' }
    throw error
  } finally {
    // After a release-all the entry is not ours any more: a newer request for the
    // same id may be registered, and deleting it would let a third one start beside it.
    if (generation === cacheGeneration) inFlight.delete(id)
  }
}

/**
 * Look a background image up, distinguishing "gone" from "could not ask".
 *
 * `revalidate` decides whether the page-lifetime cache may answer. It must be
 * **true wherever the image is about to be applied**, and false for anything
 * cosmetic (thumbnails, the preview cover).
 *
 * Without it the cache is a permanent answer: an admin deletes a company
 * background, the user's picker correctly stops offering it — the list is
 * fetched fresh — but the applied image keeps resolving from this map, so the
 * deleted background survives every rejoin short of a full page reload. Fixing
 * the HTTP caching on the route did not help, because with a cache hit here the
 * request was never made at all.
 *
 * Revalidation is cheap: the route sends `no-cache` with an ETag, so the wire
 * carries a conditional request and a 304, and the bytes come from the browser's
 * cache.
 */
export async function lookupBackgroundImage(
  id: string,
  { revalidate = false }: { revalidate?: boolean } = {}
): Promise<BackgroundImageLookup> {
  const cached = objectUrlCache.get(id)
  if (cached && !revalidate) return { status: 'ok', url: cached }

  // Several surfaces can ask for the same image at once — the thumbnail grid and
  // the processor, typically. One request serves them all.
  const pending = inFlight.get(id)
  if (pending) return pending

  const request = fetchBackgroundImage(id)
  inFlight.set(id, request)
  return request
}

/**
 * The cached-or-fetched URL, or `null` for anything else.
 *
 * Kept for callers that only want a picture and have no opinion about why they
 * did not get one. Anything that acts on absence must use
 * {@link lookupBackgroundImage} and check for `gone` specifically.
 */
export async function resolveBackgroundImageUrl(id: string): Promise<string | null> {
  const result = await lookupBackgroundImage(id)
  return result.status === 'ok' ? result.url : null
}

/** Drop one cached object URL — used after a delete. */
export function releaseBackgroundImageUrl(id: string): void {
  const url = objectUrlCache.get(id)
  if (!url) return
  URL.revokeObjectURL(url)
  objectUrlCache.delete(id)
}

/**
 * Drop every cached object URL: the user signed out.
 *
 * Without it the blobs of a signed-out account stay in memory for as long as
 * the page lives, and the next account's session reuses them by id. Downloads
 * still running are forgotten as well (see `cacheGeneration`), so none lands
 * in the emptied cache afterwards. The remembered background names go too: they
 * are the account's own titles, and a notice for the next account must not
 * quote one.
 */
export function releaseAllBackgroundImageUrls(): void {
  cacheGeneration += 1
  backgroundNames.clear()
  objectUrlCache.forEach((url) => URL.revokeObjectURL(url))
  objectUrlCache.clear()
  inFlight.clear()
}
